import React, { useState } from 'react';
import { FaBell, FaExclamationCircle, FaInfoCircle, FaCheckCircle, FaGift, FaFilter, FaCheck } from 'react-icons/fa';

const SellerNotices = () => {
    const [filter, setFilter] = useState('all');
    const [notices, setNotices] = useState([
        {
            id: 1,
            type: 'important',
            title: 'GST Invoice Update Required',
            message: 'Please update your GST invoice format to comply with new regulations effective from January 1, 2025.',
            date: '2024-12-10',
            read: false,
        },
        {
            id: 2,
            type: 'info',
            title: 'New Shipping Partner Added',
            message: 'We have partnered with BlueDart for faster deliveries. Enable it in your shipping settings.',
            date: '2024-12-09',
            read: false,
        },
        {
            id: 3,
            type: 'success',
            title: 'Payout Processed Successfully',
            message: 'Your payout of ₹45,600 has been processed and should reflect in your bank account within 2-3 days.',
            date: '2024-12-08',
            read: true,
        },
        {
            id: 4,
            type: 'promo',
            title: 'Diwali Sale - Boost Your Visibility',
            message: 'Participate in our Diwali Sale event to get featured on the homepage. Deadline: November 1.',
            date: '2024-10-15',
            read: true,
        },
        {
            id: 5,
            type: 'warning',
            title: 'Low Stock Alert',
            message: '3 of your products are running low on stock. Update your inventory to avoid order cancellations.',
            date: '2024-12-07',
            read: false,
        },
    ]);

    const getTypeIcon = (type) => {
        switch (type) {
            case 'important': return <FaExclamationCircle className="text-red-500" />;
            case 'info': return <FaInfoCircle className="text-blue-500" />;
            case 'success': return <FaCheckCircle className="text-green-500" />;
            case 'promo': return <FaGift className="text-purple-500" />;
            case 'warning': return <FaExclamationCircle className="text-yellow-500" />;
            default: return <FaBell className="text-gray-500" />;
        }
    };

    const getTypeBg = (type) => {
        switch (type) {
            case 'important': return 'border-l-red-500 bg-red-50';
            case 'info': return 'border-l-blue-500 bg-blue-50';
            case 'success': return 'border-l-green-500 bg-green-50';
            case 'promo': return 'border-l-purple-500 bg-purple-50';
            case 'warning': return 'border-l-yellow-500 bg-yellow-50';
            default: return 'border-l-gray-500 bg-gray-50';
        }
    };

    const markAsRead = (id) => {
        setNotices(notices.map(n => n.id === id ? { ...n, read: true } : n));
    };

    const markAllAsRead = () => {
        setNotices(notices.map(n => ({ ...n, read: true })));
    };

    const filteredNotices = filter === 'all' ? notices :
        filter === 'unread' ? notices.filter(n => !n.read) :
            notices.filter(n => n.type === filter);

    const unreadCount = notices.filter(n => !n.read).length;

    return (
        <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Notices & Announcements</h1>
                    <p className="text-gray-600">Stay updated with important platform notifications</p>
                </div>
                {unreadCount > 0 && (
                    <button
                        onClick={markAllAsRead}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        <FaCheck /> Mark All as Read ({unreadCount})
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex gap-2 flex-wrap">
                    {['all', 'unread', 'important', 'info', 'success', 'promo', 'warning'].map((type) => (
                        <button
                            key={type}
                            onClick={() => setFilter(type)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === type
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                            {type === 'unread' && unreadCount > 0 && (
                                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Notices List */}
            <div className="space-y-4">
                {filteredNotices.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                        No notices found
                    </div>
                ) : (
                    filteredNotices.map((notice) => (
                        <div
                            key={notice.id}
                            className={`bg-white rounded-lg shadow overflow-hidden border-l-4 ${getTypeBg(notice.type)} ${!notice.read ? 'ring-2 ring-blue-200' : ''}`}
                        >
                            <div className="p-4">
                                <div className="flex items-start gap-4">
                                    <div className="mt-1">
                                        {getTypeIcon(notice.type)}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className={`font-semibold ${!notice.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                                {notice.title}
                                            </h3>
                                            {!notice.read && (
                                                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded">New</span>
                                            )}
                                        </div>
                                        <p className="text-gray-600 mt-1">{notice.message}</p>
                                        <div className="flex items-center justify-between mt-3">
                                            <span className="text-sm text-gray-500">{notice.date}</span>
                                            {!notice.read && (
                                                <button
                                                    onClick={() => markAsRead(notice.id)}
                                                    className="text-sm text-blue-600 hover:text-blue-800"
                                                >
                                                    Mark as read
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SellerNotices;
